import axios from "axios";
import { toast } from "react-toastify";
import { appConfig } from "../../config";
import history from "../../history";
import AzureAppSettingsService from "../../services/AzureAppSettingsService";
import AzureAuthService from "../../services/AzureAuthService";
import KeycloakAppSettingsService from "../../services/KeycloakAppSettingsService";
import KeycloakAuthService from "../../services/KeycloakAuthService";

/* Check Pre Configuration */

const AppPrecheck = () => {
  // First check if SSO is undefined
  if (appConfig.SSO === undefined) {
    return false;
  }

  // Check if SSO is configured for Azure
  if (appConfig.SSO === "azure") {
    return AzurePrecheck();
  }

  // check if SSO is configured for Keycloak
  if (appConfig.SSO === "keycloak") {
    return KeycloakPrecheck();
  }

  return false;
};

const KeycloakPrecheck = () => {
  // Check if Keycloak is configured
  return true;
};
const AzurePrecheck = () => {
  // Check if Azure is configured
  const a = appConfig.azure;
  const keys = [
    'REACT_APP_MSAL_CLIENT_ID',
    'REACT_APP_MSAL_CLIENT_SCOPE',
    'REACT_APP_MSAL_TENANT_AUTHORITY_URI',
    'REACT_APP_MSAL_CACHE_LOCATION',
    'REACT_APP_MSAL_AUTH_STATE_IN_COOKIE',
    'REACT_APP_MSAL_LOGIN_REDIRECT_URI',
  ];
  for (const k of keys) {
    if (a[k] === undefined || a[k] === '') {
      return false;
    }
  }
  return true;
};

/*  MSAL SERVICE CREATION */
var appSettings;
var AuthServiceInstance;

if (appConfig.SSO === "azure") {
  appSettings = new AzureAppSettingsService();
  AuthServiceInstance = new AzureAuthService(appSettings);
}
if (appConfig.SSO === "keycloak") {
  appSettings = new KeycloakAppSettingsService();
  AuthServiceInstance = new KeycloakAuthService(appSettings);
}
/*  END MSAL SERVICE CREATION */

/* API Service Settings */
var axiosServerInstance = new axios.create();
axiosServerInstance.defaults.baseURL = appSettings.GetWebApiBaseUri();

/* INJECT BEARER TOKEN */
axiosServerInstance.interceptors.request.use(
  async (config) => {
    // Skip token injection for public endpoints
    const isPublic = config.url && (
      config.url.includes('/api/public/app-vars') ||
      config.url.includes('/api/elevated/Branding/')
    );

    if (!isPublic) {
      let response = await AuthServiceInstance.GetToken();
      if (response && response.accessToken) {
        config.headers.Authorization = `Bearer ${response.accessToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
/* END TOKEN */

/* Format response body */
const responseBody = (response) => response.data;

/* Temp function to simulate server network delay */
// const sleep = (ms) => (response) =>
//   new Promise((resolve) => setTimeout(() => resolve(response), ms));

/* TYPES OF REQUESTES SUPPORTED */
const requests = {
  get: (url) => axiosServerInstance.get(url).then(responseBody),
  post: (url, body) => axiosServerInstance.post(url, body).then(responseBody),
  put: (url, body) => axiosServerInstance.put(url, body).then(responseBody),
  del: (url) => axiosServerInstance.delete(url).then(responseBody),
};
/* END REQUEST TYPES */

/* API ERROR HANDLING */
axiosServerInstance.interceptors.response.use(undefined, (error) => {
  //console.error(error);
  if (!error.response) {
    toast.error(
      "Network Error : Can't connect to server. Displaying locally cached data. New changes wont be saved."
    );
    throw error;
  } else {
    try {
      const { status, data, config } = error.response;
      /* ALL 404 Errors are redirected to not found component */
      if (status === 404) {
        console.error("404 RESOURCE NOT FOUND");
        //history.push("/notfound");
      }

      /* 400 Errors for invalid GUID should also be redirected to not found */
      if (
        status === 400 &&
        config.method === "get" &&
        data.errors.hasOwnProperty("id")
      ) {
        history.push("/notfound");
      } else if (status === 400 && data !== null) {
        console.error("----AGENT----");
        console.error(typeof data);
        if (typeof data === "string") {
          console.error("AGENT: Intercepted 400 " + data);
          toast.error("400 The Request Failed : " + data);
        } else {
          console.error("AGENT: Intercepted 400 " + data?.title);
          toast.error("400 The Request Failed : " + data?.title);
        }
      } else if (status === 400 && data === null) {
        console.error("AGENT: Intercepted 400 Bad request");
        toast.error("400 Bad request");
      }

      /* 403 Unauthorized error */
      if (status === 403) {
        console.error("AGENT: Intercepted 403");
        toast.error(
          "Unauthorized: You do not have necessary permissions to apply this change. Please contact Daikon Administrator"
        );
      }

      if (status === 401) {
        console.error("AGENT: Intercepted 403");
        console.error(
          "Unauthorized: You do not have necessary permissions to apply this change. Please contact Daikon Administrator"
        );
      }

      /* 500 Errors */
      if (status === 500) {
        console.error("AGENT: Intercepted 500");
        toast.error("Server Error");
      }
    } catch (e) {
    } finally {
      console.error("----AGENT---- THROWING error");
      console.error(error.response);
      throw error.response;
    }
  }
});
/* END ERROR HANDLING */

/* APIS */
const Gene = {
  list: () => requests.get("/gene"),
  view: (id) => requests.get(`/gene/${id}`),
  validateTargetName: (strainID, name) =>
    requests.get(`/gene/validate-new-target-name/${strainID}/${name}/`),
  viewByAccessionNo: (accessionNo) =>
    requests.get(`/gene/by-accession/${accessionNo}`),
  edit: (newGene) => requests.post(`/gene/${newGene.id}`, newGene),
  history: (id) => requests.get(`/gene/${id}/history`),
  promotionQuestions: () => requests.get(`/geneconfig/promote/questionaire`),
  searchByIdGeneGroup: (geneId) =>
    requests.get(`/geneconfig/groups/search-by-gene-id/${geneId}`),
  submitPromotionQuestionaire: (targetName, data) =>
    requests.post(`/gene/promotionrequest/${targetName}`, data),
  editEssentiality: (geneId, essentialityId, modEssentiality) =>
    requests.post(
      `/gene/${geneId}/essentiality/${essentialityId}`,
      modEssentiality
    ),
  addEssentiality: (geneId, newEssentiality) =>
    requests.post(`/gene/${geneId}/essentiality`, newEssentiality),

  addProteinProduction: (geneId, newProteinProduction) =>
    requests.post(`/gene/${geneId}/proteinproduction`, newProteinProduction),

  editProteinProduction: (geneId, proteinproductionId, modProteinProduction) =>
    requests.post(
      `/gene/${geneId}/proteinproduction/${proteinproductionId}`,
      modProteinProduction
    ),

  addProteinProductionAssasy: (geneId, newProteinProductionAssasy) =>
    requests.post(
      `/gene/${geneId}/proteinactivityassay`,
      newProteinProductionAssasy
    ),

  editProteinProductionAssasy: (
    geneId,
    proteinProductionAssasyId,
    modProteinProductionAssasy
  ) =>
    requests.post(
      `/gene/${geneId}/proteinactivityassay/${proteinProductionAssasyId}`,
      modProteinProductionAssasy
    ),

  addCRISPRiStrain: (geneId, newCRISPRiStrain) =>
    requests.post(`/gene/${geneId}/crispristrain`, newCRISPRiStrain),

  editCRISPRiStrain: (geneId, cRISPRiStrainId, modCRISPRiStrain) =>
    requests.post(
      `/gene/${geneId}/crispristrain/${cRISPRiStrainId}`,
      modCRISPRiStrain
    ),

  addResistanceMutation: (geneId, newResistanceMutation) =>
    requests.post(`/gene/${geneId}/resistancemutation`, newResistanceMutation),
  editResistanceMutation: (
    geneId,
    resistanceMutationId,
    modResistanceMutation
  ) =>
    requests.post(
      `/gene/${geneId}/resistancemutation/${resistanceMutationId}`,
      modResistanceMutation
    ),

  addUnpublishedStructure: (geneId, newUnpublishedStructure) =>
    requests.post(
      `/gene/${geneId}/unpublishedstructures`,
      newUnpublishedStructure
    ),
  editUnpublishedStructure: (
    geneId,
    unpublishedStructureId,
    modUnpublishedStructure
  ) =>
    requests.post(
      `/gene/${geneId}/unpublishedstructures/${unpublishedStructureId}`,
      modUnpublishedStructure
    ),
  addVulnerability: (geneId, newVulnerability) =>
    requests.post(`/gene/${geneId}/vulnerability`, newVulnerability),
  editVulnerability: (geneId, vulnerabilityId, modVulnerability) =>
    requests.post(
      `/gene/${geneId}/vulnerability/${vulnerabilityId}`,
      modVulnerability
    ),
  addHypomorph: (geneId, newHypomorph) =>
    requests.post(`/gene/${geneId}/hypomorph`, newHypomorph),

  editHypomorph: (geneId, hypomorphId, modHypomorph) =>
    requests.post(`/gene/${geneId}/hypomorph/${hypomorphId}`, modHypomorph),
};

const GeneAdmin = {
  promotionRequests: () => requests.get("/elevated/gene/promotionrequests"),
  createGeneGroup: (geneGroup) =>
    requests.post(`/elevated/gene/groups`, geneGroup),
  listGeneGroups: () => requests.get(`/elevated/gene/groups`),
};

const User = {
  current: () => requests.get("/account"),
  register: (user) => requests.post(`/account/register`, user),
};

const Admin = {
  userList: () => requests.get("/elevated/accounts"),
  modifyUser: (user) => requests.post(`/elevated/accounts/${user.id}`, user),
  addUser: (user) => requests.post(`/elevated/accounts/`, user),
};

const Accounts = {
  listRoles: () => requests.get("/elevated/accounts/roles"),
  listOrgs: () => requests.get("/elevated/accounts/orgs"),
  editOrg: (id, org) => requests.post(`/elevated/accounts/orgs/${id}`, org),
  createOrg: (newOrg) => requests.post(`/elevated/accounts/orgs`, newOrg),
  listAccounts: () => requests.get("/elevated/accounts"),
  details: (email) => requests.get(`/elevated/accounts/${email}`),
  createAccount: (user) => requests.post(`/elevated/accounts/`, user),
  editAccount: (user) => requests.post(`/elevated/accounts/${user.id}`, user),
};

const Target = {
  list: () => requests.get(`/target/`),
  details: (id) => requests.get(`/target/${id}`),
  history: (id) => requests.get(`/target/${id}/history`),
  editSummary: (updatedTargetSummary) =>
    requests.post(
      `/target/${updatedTargetSummary.id}/summary`,
      updatedTargetSummary
    ),
  editPromotionData: (id, promotionDataDTOs) =>
    requests.post(`/target/${id}/promotion-info`, promotionDataDTOs),
};

const TargetAdmin = {
  create: (data) => requests.post(`/elevated/target`, data),
  import: (data) => requests.post(`/elevated/target/import`, data),
  importComplex: (data) =>
    requests.post(`/elevated/target/importComplex`, data),
  details: (id) =>
    requests.get(`/target/${id}`),
  edit: (updatedTarget) =>
    requests.post(`/elevated/target/${updatedTarget.id}`, updatedTarget),
};

const Screen = {
  list: () => requests.get(`/screen/`),
  listPhenotypic: () => requests.get(`/screen/phenotypic`),
  details: (id) => requests.get(`/screen/${id}`),
  create: (newScreen) => requests.post(`/screen`, newScreen),
  createPhenotypic: (newScreen) =>
    requests.post(`/screen/phenotypic`, newScreen),
  createSequence: (screenId, newSequence) =>
    requests.post(`/screensequence/${screenId}`, newSequence),
  merge: (mergeIds) => requests.post(`/elevated/screen/merge`, mergeIds),
  edit: (id, editedScreen) =>
    requests.post(`/elevated/screen/${id}/edit`, editedScreen),
};

const Hit = {
  create: (newHit) => requests.post(`/hit/`, newHit),
  update: (hitId, updatedHit) => requests.post(`/hit/${hitId}`, updatedHit),
};

const Discussion = {
  list: (reference) => requests.get(`/Discussion/${reference}`),
  new: (discussion) => requests.post(`/Discussion/`, discussion),
  edit: (id, discussion) => requests.post(`/Discussion/${id}`, discussion),
  delete: (id) => requests.del(`/Discussion/${id}`),
  reply: (discussionId, reply) =>
    requests.post(`/Discussion/${discussionId}/reply`, reply),
  editReply: (discussionId, replyId, reply) =>
    requests.post(`/Discussion/${discussionId}/reply/${replyId}`, reply),
  deleteReply: (discussionId, replyId) =>
    requests.del(`/Discussion/${discussionId}/reply/${replyId}`),
};

const Horizon = {
  generate: (targetName) => requests.get(`/Horizon/${targetName}`),
  generateByAccession: (accessionNo) =>
    requests.get(`/Horizon/by-accession/${accessionNo}`),
};

const General = {
  appVars: () => requests.get(`/public/app-vars`),
};

const Projects = {
  edit: (id, project) => requests.post(`/elevated/project/${id}`, project),
  terminate: (id, project) =>
    requests.post(`/elevated/project/${id}/terminate`, project),
  createHA: (newHA) => requests.post(`/elevated/project/`, newHA),
  createH2L: (id, h2lInfo) =>
    requests.post(`/elevated/project/${id}/createH2L`, h2lInfo),
  createLO: (id, lOlInfo) =>
    requests.post(`/elevated/project/${id}/createLO`, lOlInfo),
  createSP: (id, sPlInfo) =>
    requests.post(`/elevated/project/${id}/createSP`, sPlInfo),
  createIND: (id, iNDInfo) =>
    requests.post(`/elevated/project/${id}/createIND`, iNDInfo),
  createP1: (id, p1Info) =>
    requests.post(`/elevated/project/${id}/createP1`, p1Info),
  list: () => requests.get(`/project`),
  details: (id) => requests.get(`/project/${id}`),
  getcompoundevolution: (projectId) =>
    requests.get(`/project/${projectId}/compoundevolution`),
  addcompoundevolution: (projectId, newCompoundEvolution) =>
    requests.post(
      `/project/${projectId}/compoundevolution`,
      newCompoundEvolution
    ),
  editCompoundevolution: (
    projectId,
    compoundEvoluitionId,
    editedCompoundEvolution
  ) =>
    requests.post(
      `/project/${projectId}/compoundevolution/${compoundEvoluitionId}`,
      editedCompoundEvolution
    ),
  setPriorityProbability: (Id, ppDTO) => requests.post(`/project/${Id}`, ppDTO),
  createUnlinked: (newProject) =>
    requests.post(`/elevated/project/unlinked`, newProject),
  stageOverride: (id, overrideDTO) =>
    requests.post(`/elevated/project/${id}/override-stage`, overrideDTO),
  editSupportingOrg: (id, editSupportingOrgsDTO) =>
    requests.post(`/project/${id}/edit-supporting-org`, editSupportingOrgsDTO),
  editPredictedDated: (id, projectDTO) =>
    requests.post(`/project/${id}/edit-predicted-dates`, projectDTO),

  listMembers: (projectId) => requests.get(`/elevated/projectmember/${projectId}`),
  addMember: (projectId, member) => requests.post(`/elevated/projectmember/${projectId}`, member),
  removeMember: (projectId, memberEmail) => requests.delete(`/elevated/projectmember/${projectId}/${memberEmail}`),
};

const Vote = {
  castVote: (vote) => requests.post(`/vote/${vote.voteId}/`, vote),
  enableVoting: (voteIds) => requests.post(`/elevated/vote/enable/`, voteIds),
  freezeVoting: (voteIds) => requests.post(`/elevated/vote/freeze/`, voteIds),
};

const DataView = {
  targetDash: () => requests.get(`/data-view/VTarget/dash-view`),
};

const Compounds = {
  list: () => requests.get(`/compound/`),
  listByProject: (projectId) => requests.get(`/compound/?projectId=${projectId}`),
  details: (id) => requests.get(`/data-view/VCompound/details/${id}`),
  edit: (id, compound) => requests.post(`/compound/${id}/edit`, compound),
  editExternalId: (id, compound) =>
    requests.post(`/compound/${id}/edit-external-id`, compound),
  addActivity: (compoundId, activity) =>
    requests.post(`/compound/${compoundId}/activity`, activity),
  editActivity: (activityId, activity) =>
    requests.put(`/compound/activity/${activityId}`, activity),
  deleteActivity: (activityId) =>
    requests.del(`/compound/activity/${activityId}`),
  delete: (id) => requests.del(`/compound/${id}`),
  batchDelete: (ids) => requests.post(`/compound/batch-delete`, ids),
  importCsv: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    return axiosUploadInstance.post(`/compound/import-csv?projectId=${projectId}`, formData).then(responseBody);
  },
  listDataSets: (projectId) => requests.get(`/compound/datasets?projectId=${projectId}`),
  createDataSet: (body) => requests.post(`/compound/datasets`, body),
  deleteDataSet: (id) => requests.del(`/compound/datasets/${id}`),
  listColumns: (projectId) => requests.get(`/compound/columns?projectId=${projectId}`),
  saveColumns: (projectId, body) => requests.post(`/compound/columns?projectId=${projectId}`, body),

  // ── Compound Sub-Table API ──
  subTable: {
    list: (tableType, projectId) => requests.get(`/compound/sub-table/${tableType}?projectId=${projectId}`),
    save: (tableType, body) => requests.post(`/compound/sub-table/${tableType}`, body),
    delete: (tableType, id) => requests.del(`/compound/sub-table/${tableType}/${id}`),
    listColumns: (tableType, projectId) => requests.get(`/compound/sub-table/${tableType}/columns?projectId=${projectId}`),
    saveColumns: (tableType, projectId, body) => requests.post(`/compound/sub-table/${tableType}/columns?projectId=${projectId}`, body),
    importCsv: (tableType, projectId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      formData.append('tableType', tableType);
      return axiosUploadInstance.post(`/compound/sub-table/${tableType}/import-csv?projectId=${projectId}`, formData).then(responseBody);
    },
    listDesignIds: (projectId) => requests.get(`/compound/sub-table/design-ids?projectId=${projectId}`),
    // Activity-specific: assay column definitions
    assayColumns: {
      list: (projectId) => requests.get(`/compound/sub-table/activity/columns?projectId=${projectId}`),
      create: (body) => requests.post(`/compound/sub-table/activity/columns`, body),
      delete: (id) => requests.del(`/compound/sub-table/activity/columns/${id}`),
      update: (body) => requests.post(`/compound/sub-table/activity/columns/update`, body),
      reorder: (body) => requests.post(`/compound/sub-table/activity/columns/reorder`, body),
    },
    // Activity-specific: batch save values for a compound
    saveActivityValues: (body) => requests.post(`/compound/sub-table/activity/values`, body),
  },
};

// ── Screening Workflow API ──
const Screening = {
  dashboard: (projectId) => requests.get("/screening/dashboard/" + projectId),
  listWorkflows: (projectId, isTemplate) => requests.get("/screening/workflows/" + projectId + (isTemplate !== undefined ? "?isTemplate=" + isTemplate : "")),
  getWorkflow: (id) => requests.get("/screening/workflow/" + id),
  saveWorkflow: (data) => requests.post("/screening/workflow", data),
  deleteWorkflow: (id) => requests.del("/screening/workflow/" + id),
  toggleShare: (id, isShared) => requests.post(`/screening/workflow/${id}/share`, isShared),
  // SSE chat stream URL (EventSource)
  chatStream: (sessionId, projectId, message) => {
    const base = appSettings?.GetWebApiBaseUri?.() || "/api";
    const params = new URLSearchParams({ projectId, message });
    if (sessionId) params.set("sessionId", sessionId);
    return `${base}/screening/chat/stream?${params}`;
  },
  // Chat session management
  listSessions: (projectId) => requests.get("/screening/chat/sessions/" + projectId),
  getSession: (sessionId) => requests.get("/screening/chat/session/" + sessionId),
};

const Organisms = {
  list: () => requests.get(`/organism/`),
  create: (newOrganism) => requests.post(`/elevated/organism/`, newOrganism),
  edit: (id, organism) => requests.post(`/elevated/organism/${id}`, organism),
  delete: (id) => requests.del(`/elevated/organism/${id}`),
};

const Strains = {
  list: () => requests.get(`/strain/`),
  create: (newStrain) => requests.post(`/elevated/strain/`, newStrain),
  edit: (id, strain) => requests.post(`/elevated/strain/${id}`, strain),
  delete: (id) => requests.del(`/elevated/strain/${id}`),
};

const axiosUploadInstance = new axios.create();
axiosUploadInstance.defaults.baseURL = appSettings.GetWebApiBaseUri();

axiosUploadInstance.interceptors.request.use(
  async (config) => {
    let response = await AuthServiceInstance.GetToken();
    config.headers.Authorization = `Bearer ${response.accessToken}`;
    config.headers["Content-Type"] = "multipart/form-data";
    return config;
  }
);

const Branding = {
  get: () => requests.get(`/elevated/branding/`),
  update: (brandingData) => requests.post(`/elevated/branding/`, brandingData),
};

const AppConfigurationsAPI = {
  list: () => requests.get(`/elevated/AppConfiguration/`),
  details: (key) => requests.get(`/elevated/AppConfiguration/${key}`),
  create: (appConf) => requests.post(`/elevated/AppConfiguration/`, appConf),
  edit: (key, appConf) =>
    requests.post(`/elevated/AppConfiguration/${key}`, appConf),
  delete: (key) =>
    requests.del(`/elevated/AppConfiguration/${key}`),
};

const AppBackgroundTaskAPI = {
  list: () => requests.get(`/elevated/AppTask/`),
};

const PatentCompounds = {
  listGroups: (projectId) => requests.get(`/elevated/PatentCompound/${projectId}/groups`),
  createGroup: (projectId, body) => requests.post(`/elevated/PatentCompound/${projectId}/groups`, body),
  deleteGroup: (id) => requests.del(`/elevated/PatentCompound/groups/${id}`),
  list: (projectId, groupId, showUngrouped) => requests.get(`/elevated/PatentCompound/${projectId}${groupId ? `?groupId=${groupId}` : ''}${showUngrouped ? `${groupId ? '&' : '?'}showUngrouped=true` : ''}`),
  save: (projectId, body) => requests.post(`/elevated/PatentCompound/${projectId}`, body),
  delete: (id) => requests.del(`/elevated/PatentCompound/${id}`),
  listColumns: (projectId, groupId) => requests.get(`/elevated/PatentCompound/${projectId}/columns${groupId ? `?groupId=${groupId}` : ''}`),
  saveColumns: (projectId, body) => requests.post(`/elevated/PatentCompound/${projectId}/columns`, body),
  importCsv: (projectId, file, groupId) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosUploadInstance.post(`/elevated/PatentCompound/${projectId}/import-csv${groupId ? `?groupId=${groupId}` : ''}`, formData).then(responseBody);
  },
};

const ReferenceCompounds = {
  listGroups: (projectId) => requests.get(`/elevated/ReferenceCompound/${projectId}/groups`),
  createGroup: (projectId, body) => requests.post(`/elevated/ReferenceCompound/${projectId}/groups`, body),
  deleteGroup: (id) => requests.del(`/elevated/ReferenceCompound/groups/${id}`),
  list: (projectId, groupId) => requests.get(`/elevated/ReferenceCompound/${projectId}${groupId ? `?groupId=${groupId}` : ''}`),
  save: (projectId, body) => requests.post(`/elevated/ReferenceCompound/${projectId}`, body),
  delete: (id) => requests.del(`/elevated/ReferenceCompound/${id}`),
  listColumns: (projectId) => requests.get(`/elevated/ReferenceCompound/${projectId}/columns`),
  saveColumns: (projectId, body) => requests.post(`/elevated/ReferenceCompound/${projectId}/columns`, body),
  importCsv: (projectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosUploadInstance.post(`/elevated/ReferenceCompound/${projectId}/import-csv`, formData).then(responseBody);
  },
};

const Batch = {
  genePoolSync: (task) => requests.post(`/elevated/batch/gene-pool-sync`, task),
};


/* REINVENT4 MOLECULAR DESIGN API */
var reinventAxiosInstance = new axios.create();
reinventAxiosInstance.defaults.baseURL = '/api/reinvent';

const reinventRequests = {
  get: (url) => reinventAxiosInstance.get(url).then(responseBody),
  post: (url, body) => reinventAxiosInstance.post(url, body).then(responseBody),
};

const Reinvent = {
  health: () => reinventRequests.get('/health'),
  listPriors: () => reinventRequests.get('/priors'),
  generate: (params) => reinventRequests.post('/generate', params),
  getTask: (taskId) => reinventRequests.get('/tasks/' + taskId),
  optimize: (params) => reinventRequests.post('/optimize', params),
  scaffold: (params) => reinventRequests.post('/scaffold', params),
};

const exports = {
  AppPrecheck,
  AuthServiceInstance,
  Accounts,
  Gene,
  GeneAdmin,
  User,
  Admin,
  Target,
  TargetAdmin,
  Screen,
  Hit,
  Discussion,
  Horizon,
  General,
  Projects,
  Vote,
  DataView,
  Compounds,
  Organisms,
  Strains,
  AppConfigurationsAPI,
  Branding,
  AppBackgroundTaskAPI,
  PatentCompounds,
  ReferenceCompounds,
  Batch,
  Reinvent,
  Screening,
};

export default exports;
